import { useEffect, useRef, useState } from 'react';
import { Check, ChevronRight, Flame } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DEMO_RECOVERY } from '@/utils/demoRecovery';
import { RecoveryAnalyticsPanel } from '@/components/RecoveryAnalyticsPanel';

const BLUE = '#0A84FF';

const Count = ({ value, suffix = '', decimals = 0 }) => {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setShown(value); return; }
    const start = performance.now(); const a = from.current; const b = value; let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / 900);
      setShown(a + (b - a) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick); else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{shown.toFixed(decimals)}{suffix}</>;
};

const Spark = ({ week, valueKey, target }) => {
  const max = Math.max(target || 0, ...week.map((d) => d[valueKey] || 0), 1);
  const sourceKey = valueKey === 'water_ml' ? 'water_source' : 'sleep_source';
  return (
    <div className="relative mt-2 h-8 flex items-end gap-[3px]">
      {target > 0 && (
        <span className="absolute left-0 right-0 border-t border-dashed border-white/20"
              style={{ bottom: `${(target / max) * 100}%` }} />
      )}
      {week.map((d) => {
        const v = d[valueKey] || 0;
        return (
          <div key={d.date} className="flex-1 rounded-sm"
               style={{
                 height: `${Math.max((v / max) * 100, v ? 6 : 3)}%`,
                 background: v ? BLUE : 'rgba(255,255,255,0.10)',
                 boxShadow: target > 0 && v >= target ? `0 0 8px ${BLUE}` : 'none',
                 opacity: d[sourceKey] === 'auto' ? 0.5 : 1,
               }} />
        );
      })}
    </div>
  );
};

const Block = ({ img, label, children, week, valueKey, target, testId }) => (
  <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] p-3" data-testid={testId}>
    <img src={img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <span className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(4,6,10,0.78) 0%, rgba(4,6,10,0.88) 100%)' }} />
    <div className="relative flex h-full flex-col">
      <p className="text-[10px] uppercase tracking-[0.14em] text-white/45 leading-[1.25]">{label}</p>
      <div className="mt-auto pt-1">
        <p className="font-heading font-black text-white text-lg leading-tight">{children}</p>
        <Spark week={week} valueKey={valueKey} target={target} />
      </div>
    </div>
  </div>
);

const PART_LABELS = { water: 'Water', meal: 'Meal', sleep: 'Sleep' };

/** Today's three-part checklist — all three in means the day counts for the streak. */
const TodayChecklist = ({ parts }) => (
  <div className="mt-3 flex items-center gap-1.5" data-testid="recovery-today-checklist">
    {Object.keys(PART_LABELS).map((key) => {
      const done = !!parts?.[key];
      return (
        <span key={key}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border text-[11px] font-heading font-bold"
          style={{
            borderColor: done ? 'rgba(10,132,255,0.45)' : 'rgba(255,255,255,0.10)',
            background: done ? 'rgba(10,132,255,0.12)' : 'rgba(255,255,255,0.03)',
            color: done ? '#9CCBFF' : 'rgba(255,255,255,0.40)',
          }}
          data-testid={`recovery-check-${key}${done ? '-done' : ''}`}>
          {done ? <Check className="w-3 h-3" /> : <span className="w-3 text-center">–</span>}
          {PART_LABELS[key]}
        </span>
      );
    })}
  </div>
);

/** Weekly recovery roll-up for the home page. */
export const RecoveryWeekCard = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(false);
  const [mounted, setMounted] = useState(false); // panel only loads once it's been opened
  const openAnalytics = () => { setMounted(true); setAnalytics(true); };

  // Deep link from a coach reply: /dashboard?open=recovery
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('open') !== 'recovery') return;
    const t = window.setTimeout(openAnalytics, 400);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => api
      .get('/recovery/week', { headers: { 'X-TZ-Offset': String(new Date().getTimezoneOffset()) } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => {});
    load();
    window.addEventListener('recovery:changed', load);
    return () => { cancelled = true; window.removeEventListener('recovery:changed', load); };
  }, [api]);

  if (!data) return null;
  // No real logs yet: show a sample week rather than an empty shell, with a small note so it isn't mistaken for real data.
  const view = data.has_data ? data : { ...DEMO_RECOVERY, targets: data.targets };
  const t = view.totals || {}; const g = view.targets || {}; const week = view.week || [];
  const streak = view.streak || { current: 0, best: 0, today_parts: {}, missing: [] };

  return (
    <>
    <div className="rounded-3xl border-2 border-white/[0.18] p-4 cursor-pointer active:scale-[0.99] transition-transform"
         role="button" tabIndex={0}
         onClick={openAnalytics}
         onKeyDown={(e) => { if (e.key === 'Enter') openAnalytics(); }}
         style={{ background: 'linear-gradient(135deg, #0C1218 0%, #07090C 60%, #060A12 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 16px 40px rgba(0,0,0,0.45)' }}
         data-testid="recovery-week-card">
      <div className="flex items-start gap-3">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl p-[2px]"
              style={{ backgroundImage: 'linear-gradient(135deg,#7CD4FF 0%,#0A84FF 50%,#0B2A6B 100%)', boxShadow: '0 0 22px rgba(10,132,255,0.35)' }}>
          <img src="/recovery-icon.webp" alt="" width={56} height={56} loading="lazy" decoding="async"
               className="h-full w-full rounded-[14px] object-cover" data-testid="recovery-header-icon" />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="font-heading text-[26px] font-black leading-none tracking-tight">
            <span className="text-white">Recovery </span>
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg,#5AC8FA,#0A84FF)' }}>Score</span>
          </h3>
          <p className="mt-1.5 text-[13px] text-white/50">Water, food and sleep this week</p>
        </div>
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-white/45">Recovery this week</p>
          <p className="font-heading font-black text-white text-[clamp(2rem,9vw,2.5rem)] leading-none" data-testid="recovery-score">
            <Count value={view.score || 0} />
            <span className="text-base text-white/35 font-normal"> / 100</span>
          </p>
        </div>
        {streak.current > 0 ? (
          <span className="shrink-0 mt-1 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-heading font-black uppercase tracking-[0.1em]"
                style={{ borderColor: 'rgba(10,132,255,0.5)', background: 'rgba(10,132,255,0.14)', color: '#9CCBFF' }}
                data-testid="recovery-streak-badge">
            <Flame className="w-3.5 h-3.5" />
            {streak.current} day{streak.current === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="shrink-0 mt-1 text-[11px] font-heading font-bold uppercase tracking-[0.12em]" style={{ color: BLUE }}
                data-testid="recovery-streak-badge">
            {'Tracking'}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-white/55" data-testid="recovery-read">{view.read}</p>

      <TodayChecklist parts={streak.today_parts} />
      <p className="mt-1.5 text-xs text-white/40" data-testid="recovery-streak-hint">
        {streak.today_complete
          ? `Today counts${streak.best > streak.current ? ` · best run ${streak.best} days` : ' · your best run yet'}`
          : streak.current > 0
            ? `${(streak.missing || []).map((m) => PART_LABELS[m]).join(' and ')} left to keep the ${streak.current}-day run`
            : 'Water, a meal and sleep in one day starts a streak — days you skip entirely never break it'}
      </p>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Block img="/recovery-hydrate.jpg?v=3" label="Water" week={week} valueKey="water_ml" target={g.water_ml} testId="recovery-block-water">
          <Count value={(t.water_ml || 0) / 1000} suffix="L" decimals={1} />
        </Block>
        <Block img="/recovery-refuel.jpg?v=3" label="Protein / day" week={week} valueKey="protein_g" target={g.protein_g} testId="recovery-block-food">
          <Count value={t.avg_protein_g || 0} suffix="g" />
        </Block>
        <Block img="/recovery-sleep.jpg?v=3" label="Sleep / night" week={week} valueKey="sleep_hours" target={g.sleep_hours} testId="recovery-block-sleep">
          <Count value={t.avg_sleep_hours || 0} suffix="h" decimals={1} />
        </Block>
      </div>

      <p className="mt-3 text-xs text-white/40" data-testid="recovery-week-footnote">
        {t.meals || 0} meals logged · {t.avg_calories || 0} kcal/day average
      </p>
      {!data.has_data && (
        <p className="mt-1 text-xs text-white/40" data-testid="recovery-week-sample-note">
          Sample week, log today to see your real numbers.
        </p>
      )}
      <div className="mt-3 pt-3 border-t border-white/[0.08] flex items-center justify-between gap-2"
           data-testid="recovery-view-more">
        <span className="min-w-0">
          <span className="block font-heading font-black uppercase tracking-tight text-white text-sm">
            View full analytics
          </span>
          <span className="block text-[11px] text-white/45">
            Charts, targets and exactly what's left today — tap anywhere
          </span>
        </span>
        <ChevronRight className="w-5 h-5 shrink-0" style={{ color: BLUE }} />
      </div>
      {/* Portal content bubbles through the React tree, so the panel sits outside the tappable card. */}
    </div>
    {mounted && <RecoveryAnalyticsPanel open={analytics} onOpenChange={setAnalytics} />}
    </>
  );
};

export default RecoveryWeekCard;
